import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { BadgeCheck, HeartHandshake, Lock, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { PageLoading } from '@/components/ui/Spinner';
import { FeedbackButton } from '@/components/ui/FeedbackButton';
import { Logo, SiteFooter, ThemeToggle } from '@/components/ui';

const TRUST_POINTS = [
  {
    icon: BadgeCheck,
    title: 'Identidade verificada',
    text: 'Clubes, atletas e empresas só aparecem publicamente depois do KYC aprovado.',
  },
  {
    icon: ShieldCheck,
    title: 'Audiência com origem clara',
    text: 'Cada número mostra se foi declarado pelo perfil ou verificado pelo Instagram.',
  },
  {
    icon: HeartHandshake,
    title: 'Negociação direta',
    text: 'A plataforma conecta. O acordo e o pagamento ficam entre as partes.',
  },
];

export function AuthLayout() {
  const { user, isLoading, pendingGoogleSignup } = useAuthStore();
  const location = useLocation();

  if (isLoading) return <PageLoading />;
  if (user) return <Navigate to="/dashboard" replace />;
  // Conta Google autenticada sem cadastro: só a etapa de completar cadastro faz sentido
  if (pendingGoogleSignup && location.pathname !== '/completar-cadastro') {
    return <Navigate to="/completar-cadastro" replace />;
  }

  return (
    <div className="flex min-h-screen bg-canvas">
      <div className="relative flex min-w-0 flex-1 flex-col">
        {/* Faixa curta no mobile/tablet no lugar do painel lateral. */}
        <div className="flex items-center gap-2 bg-brand-900 px-4 py-2.5 text-xs font-medium text-white/85 lg:hidden">
          <Lock className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
          Ambiente seguro · perfis públicos só com KYC aprovado
        </div>

        <div className="absolute right-4 top-12 z-10 sm:right-8 lg:top-6">
          <ThemeToggle />
        </div>

        <main id="conteudo" className="flex flex-1 items-center justify-center px-4 py-6 sm:px-8">
          <div className="w-full max-w-[420px]">
            <Logo className="mb-6" />
            <Outlet />
          </div>
        </main>

        {/* Espaço inferior extra para o botão flutuante de feedback não cobrir os links do rodapé. */}
        <SiteFooter className="border-line/60 pb-20 sm:pb-16 lg:pb-0 [&>div]:max-w-none" />
      </div>

      <aside className="relative hidden w-5/12 max-w-[640px] shrink-0 overflow-hidden bg-brand-900 text-white lg:flex lg:items-center">
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]" aria-hidden="true">
          <svg className="h-full w-full" viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice" fill="none" stroke="white" strokeWidth="1.5">
            <rect x="30" y="30" width="340" height="540" rx="4" />
            <line x1="30" y1="300" x2="370" y2="300" />
            <circle cx="200" cy="300" r="60" />
            <rect x="110" y="30" width="180" height="90" />
            <rect x="110" y="480" width="180" height="90" />
          </svg>
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-900/40 via-transparent to-brand-900/80" aria-hidden="true" />

        <div className="relative mx-auto w-full max-w-md px-10 py-16 xl:px-14">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white/90">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Ambiente seguro
          </p>
          <h2 className="mt-6 font-display text-3xl font-bold leading-[1.12] text-white xl:text-4xl">
            Patrocínio esportivo com <span className="text-accent">confiança</span> dos dois lados.
          </h2>
          <ul className="mt-10 space-y-6 border-t border-white/10 pt-8">
            {TRUST_POINTS.map((point) => (
              <li key={point.title} className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                  <point.icon className="h-5 w-5 text-accent" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-white">{point.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/70">{point.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Ponto de montagem para ações flutuantes (ex.: botão "Enviar feedback"). */}
      <div id="floating-actions" className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
        <div style={{ order: 10 }}>
          <FeedbackButton />
        </div>
      </div>
    </div>
  );
}
